
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import {
    ListLayoutConfig,
    ListLayoutViewType,
    TableConfig,
    KanbanConfig,
    TimelineConfig,
    CalendarConfig,
    FilterDefinition,
    SavedFilter,
    TableState,
    PaginationData,
} from './core/list-layout.types';
import { mergeConfig, createDefaultListLayoutConfig } from './core/list-layout.config';
import { createListLayoutStore, ListLayoutActionType, ListLayoutGetterType } from './store/list-layout.store';
import { generateId } from './core/list-layout.utils';
import ListLayoutToolbar from './components/ListLayoutToolbar.vue';
import ListLayoutFilters from './components/ListLayoutFilters.vue';
import ListLayoutTable from './components/ListLayoutTable.vue';
import ListLayoutKanban from './components/ListLayoutKanban.vue';
import ListLayoutTimeline from './components/ListLayoutTimeline.vue';
import ListLayoutCalendar from './components/ListLayoutCalendar.vue';

@Component({
    components: {
        ListLayoutToolbar,
        ListLayoutFilters,
        ListLayoutTable,
        ListLayoutKanban,
        ListLayoutTimeline,
        ListLayoutCalendar,
    },
})
export default class ListLayout extends Vue {
    @Prop({ required: true }) store!: any;
    @Prop({ type: Object }) config?: Partial<ListLayoutConfig>;
    @Prop({ type: Object }) tableConfig?: TableConfig;
    @Prop({ type: Object }) kanbanConfig?: KanbanConfig;
    @Prop({ type: Object }) timelineConfig?: TimelineConfig;
    @Prop({ type: Object }) calendarConfig?: CalendarConfig;
    @Prop({ type: Array, default: () => [] }) filterDefinitions!: FilterDefinition[];

    ViewType = ListLayoutViewType;

    items: any[] = [];
    loading = false;
    pagination: PaginationData | null = null;

    private layoutStore: any = null;

    get mergedConfig(): ListLayoutConfig {
        const defaultConfig = createDefaultListLayoutConfig(this.config?.storageKey || 'default');
        return mergeConfig(defaultConfig, this.config);
    }

    get activeView(): ListLayoutViewType {
        return this.layoutStore?.useGetter(ListLayoutGetterType.ActiveView) || this.mergedConfig.defaultView;
    }

    get filters(): Record<string, any> {
        return this.layoutStore?.useGetter(ListLayoutGetterType.Filters) || {};
    }

    get savedFilters(): SavedFilter[] {
        return this.layoutStore?.useGetter(ListLayoutGetterType.SavedFilters) || [];
    }

    get tableState(): TableState {
        return this.layoutStore?.useGetter(ListLayoutGetterType.TableState) || {
            pageSize: this.tableConfig?.pageSize || 50,
            currentPage: 1,
            selectedItems: [],
        };
    }

    get showFilters(): boolean {
        return this.layoutStore?.useGetter(ListLayoutGetterType.ShowFilters) || false;
    }

    get hasSelectedItems(): boolean {
        return this.tableState.selectedItems.length > 0;
    }

    mounted() {
        this.layoutStore = createListLayoutStore(this.mergedConfig.storageKey, this.mergedConfig.defaultView);
        this.loadData();
    }

    @Watch('filters', { deep: true })
    onFiltersChange() {
        this.loadData();
    }

    @Watch('tableState.sort', { deep: true })
    onSortChange() {
        if (this.activeView === ListLayoutViewType.Table) {
            this.loadData();
        }
    }

    @Watch('tableState.currentPage')
    onPageChange() {
        if (this.activeView === ListLayoutViewType.Table) {
            this.loadData();
        }
    }

    async loadData() {
        if (!this.store) {
            return;
        }

        this.loading = true;

        try {
            if (this.activeView === ListLayoutViewType.Table && this.tableConfig?.serverSidePagination) {
                const skip = (this.tableState.currentPage - 1) * this.tableState.pageSize;
                const take = this.tableState.pageSize;

                const result = await this.store.query({
                    skip,
                    take,
                    filters: this.filters,
                    sort: this.tableState.sort,
                });

                this.items = result.data || [];
                this.pagination = {
                    total: result.total || 0,
                    skip,
                    take,
                    currentPage: this.tableState.currentPage,
                    totalPages: Math.ceil((result.total || 0) / take),
                };
            } else {
                const result = await this.store.getAll({
                    filters: this.filters,
                });
                this.items = result || [];
                this.pagination = null;
            }

            this.$emit('data-loaded', this.items);
        } catch (error) {
            console.error('Failed to load data:', error);
            this.$emit('error', error);
        } finally {
            this.loading = false;
        }
    }

    handleViewChange(view: ListLayoutViewType) {
        this.layoutStore?.dispatch(ListLayoutActionType.SetActiveView, view);
        this.loadData();
    }

    handleToggleFilters() {
        this.layoutStore?.dispatch(ListLayoutActionType.ToggleFilters, null);
    }

    handleFiltersUpdate(newFilters: Record<string, any>) {
        this.layoutStore?.dispatch(ListLayoutActionType.SetFilters, newFilters);
    }

    handleSaveFilter(filter: SavedFilter) {
        const filterToSave = {
            ...filter,
            id: filter.id || generateId(),
            createdAt: filter.createdAt || new Date(),
            updatedAt: new Date(),
        };
        this.layoutStore?.dispatch(ListLayoutActionType.SaveFilter, filterToSave);
    }

    handleLoadFilter(filterId: string) {
        this.layoutStore?.dispatch(ListLayoutActionType.LoadSavedFilter, filterId);
    }

    handleDeleteFilter(filterId: string) {
        this.layoutStore?.dispatch(ListLayoutActionType.DeleteSavedFilter, filterId);
    }

    handleTableStateUpdate(newState: Partial<TableState>) {
        this.layoutStore?.dispatch(ListLayoutActionType.SetTableState, newState);
    }

    handleItemClick(item: any) {
        this.$emit('item-click', item);
    }

    handleSelectionChange(selectedItems: string[]) {
        this.layoutStore?.dispatch(ListLayoutActionType.SetTableState, { selectedItems });
        this.$emit('selection-change', selectedItems);
    }

    handleItemMove(payload: any) {
        this.$emit('item-move', payload);
    }

    handleDateChange(payload: any) {
        this.$emit('date-change', payload);
    }

    async handleLoadMore() {
        // Implement infinite scroll logic
        this.$emit('load-more');
    }

    async handleExport(format: 'xlsx' | 'csv' | 'pdf') {
        if (!this.store.export) {
            console.warn('Export is not supported by the store');
            return;
        }

        this.loading = true;

        try {
            await this.store.export({
                format,
                filters: this.filters,
            });
            this.$emit('export-complete', format);
        } catch (error) {
            console.error('Export failed:', error);
            this.$emit('export-error', error);
        } finally {
            this.loading = false;
        }
    }
}
